import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import {
  EntrancePlantDetailDTO,
  ApartmentLabelDTO,
  ApartmentDTO,
  BuildingImportDto,
  LabelType,
  BuildingDTO,
  AddressingType,
  PasspartoutModes,
} from '../../../../../../client-generator/generated-clients/plants';
import { isCodeCorrectValue, KeypadCodeValidation, keypadCodeValidationToString, SimpleLabel, SingleLabelApartment } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import DialogInfo from '@/views/components/DialogInfo/DialogInfo.vue';

interface CheckListElement {
  data: SingleLabelApartment;
  isDisabled: boolean;
  selected: boolean;
}

interface Recap {
  buildingName: string;
  apartmentName: string;
  newScsAddr: number;
  oldScsAddr: number;
  newCode: string;
  oldCode: string;
  newLabels: ApartmentLabelDTO[];
  oldLabels: ApartmentLabelDTO[];
  newKeycode: string;
  oldKeycode: string;
  oldBadges: string[];
  newBadges: string[];
}

interface ErrorAndCause {
  error: boolean;
  importedValue?: string[];
  importedAp?: string;
  dbApartment?: string;
}

@Options({
  name: 'DialogImportFile',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    importedData: {
      type: Array as PropType<SingleLabelApartment[]>,
      required: true,
    },
    isCreationMode: {
      type: Boolean,
      default: false,
      required: false,
    },
    entrancePlantDetail: {
      type: Object as PropType<EntrancePlantDetailDTO>,
      required: true,
    },
  },
  watch: {
    isVisible: function (newValue) {
      if(newValue) {
        this.initializeData();
      } else {
        this.isDialogVisible = newValue;
      }
    }  
  },
  components: { DialogInfo },
  emits: ['hide-dialog', 'grouped', 'overwrite'],
})
export default class DialogImportFile extends Vue {
  isVisible!: boolean;
  isCreationMode!: boolean;
  importedData!: SingleLabelApartment[];
  entrancePlantDetail!: EntrancePlantDetailDTO;
  isDialogVisible = false;
  existingApartment: CheckListElement[] = [];
  //used for faster check to disable the checkbox
  labelsMap: {[buildingName: string]: {[apartmentName: string]: {labels: SimpleLabel[], badges: string[] }}} = {};
  noMatch: CheckListElement[] = [];
  overwrited: {recap: Recap, diffScs: boolean; diffLabel: boolean, diffCode: boolean, diffBadge: boolean, diffCallingCode: boolean}[] = [];
  recap: Recap[] = [];
  labelGrouped: { [buildingKey: string]: { apartmentName: string, scsAddr: number, callingCode: string, labels: SimpleLabel[], badges:string[], keypadCode: string}[] } = {};
  dialogStep = 1;
  isCallingMode = false;
  loading = false;
  creationCodeLength = 0;

  popupErrorMessage = '';
  errorsType: {scsAdrError: ErrorAndCause, callingCodeError: ErrorAndCause, keypadError: ErrorAndCause, badgesError: ErrorAndCause} = {scsAdrError: {error: false}, callingCodeError: {error: false}, keypadError: {error: false}, badgesError: {error: false}};
  popupErrorValues: ErrorAndCause = {error: false, importedValue: [], importedAp: '', dbApartment: ''};
  isPopupErrorVisible = false;

  get dialogTitle() {
    return this.dialogStep === 1 ? this.$t('labelToImport') : '';
  }

  get subtitle() {
    return this.dialogStep === 1 ? this.$t('selectLabelsUpper') : '';
  }
  
  get submitButtonLabel() {
    return this.$t("AppResource.Save");
  }

  get firstStepList() {
    if(!this.isCreationMode) {
      return this.existingApartment;
    } else {
      return this.noMatch;
    }
  }

  get enableSaveButton() {
    if (this.dialogStep === 1) {
      return this.firstStepList.some((el) => el.selected)
    } 
    return true;
  }

  async initializeData() {
    this.dialogStep = 1;
    this.existingApartment = [];
    this.noMatch = [];
    this.creationCodeLength = 0;
    this.labelsMap = {};
    this.isCallingMode = this.entrancePlantDetail.addressingType === AddressingType.DirectCall_Alphanumeric || 
            this.entrancePlantDetail.addressingType === AddressingType.DirectCall_NumbersOnly;
    this.popupErrorMessage = '';
    this.errorsType = {scsAdrError: {error: false}, callingCodeError: {error: false}, keypadError: {error: false}, badgesError: {error: false}};
    this.popupErrorValues = {error: false, importedValue: [], importedAp: '', dbApartment: ''};
    this.isPopupErrorVisible = false;

    try {
      await this.cleanSingleLabelApartment(this.importedData);
      this.isDialogVisible = this.isVisible;
    } catch (error) {
      this.doHideDialog();
    }
  }

  async cleanSingleLabelApartment(data: SingleLabelApartment[]) {
    try {
      data.forEach((item) => {
        //fill labelsMap
        if (!this.labelsMap[item.buildingName!]) {
          this.labelsMap[item.buildingName!] = {};
        }
        if (!this.labelsMap[item.buildingName!][item.apartmentName!]) {
          this.labelsMap[item.buildingName!][item.apartmentName!] = {labels: [], badges: []};
        }
        this.checkScsAddress(item);
        if (this.isCallingMode) this.checkCallingCode(item);
        this.checkBadges(item);
        this.checkKeypadCode(item);
        
        const buildingExist = this.existBuildingSameName(item.buildingName);
        if (buildingExist) {
          const apartmentExist = this.existApartmentSameName(buildingExist, item.apartmentName);
          if (apartmentExist) {
            this.existingApartment.push({
              data: item,
              isDisabled: false,
              selected: false,
            });
            return;
          } 
        }
        if(!this.isCreationMode) throw new Error(this.$t('entrancePlant.importMismatchPlantStructure'));
        this.noMatch.push({
          data: item,
          isDisabled: false,
          selected: false,
        });
      });
    } catch (error) {
      const message = (error as Error).message.split('|');
      this.$toast.add({
        severity: 'error',
        summary: message[0],
        detail: message[1],
        closable: true,
        life: 6000,
      });
      throw error;
    }
  }

  selectAll() {
    this.loading = true;
    this.processSelectAll().then(() => {
      this.loading = false;
    });
  }

  async processSelectAll() {
    return new Promise((resolve) => {
      if (this.firstStepList.filter((el) => !el.isDisabled)
        .every((el) => el.selected)) {
        this.firstStepList.forEach((el) => {el.selected = false; this.manageLabelsMap(el)});
      } else {
        this.firstStepList.forEach((el) => {
          if (!this.checkIfDisabled(el) && !el.selected) {
            el.selected = true;
            this.manageLabelsMap(el);
          }
        });  
      }
      setTimeout(() => 
      resolve(true), 300);
    });
  }

  async handleButtonSubmitAction() {
    if (this.dialogStep === 1) {
      this.groupSelected();
      this.checkErrors();
      if (this.errorsType.scsAdrError.error || this.errorsType.callingCodeError.error || this.errorsType.badgesError.error || this.errorsType.keypadError.error) {
        this.showPopupError();
        return;
      } else {
        this.buildRecap();
        if(this.overwrited.length > 0) {
          this.dialogStep = 2;
        } else {
          this.$emit('overwrite', this.prepareToSend());
        }
      }
    } else {
      this.$emit('overwrite', this.prepareToSend());
    }
  }

  groupSelected() {
    this.labelGrouped = {};
    const selectedLabels = this.firstStepList.filter((el) => el.selected).map((el) => el.data)
    selectedLabels.forEach((item: SingleLabelApartment) => {
      if (!this.labelGrouped[item.buildingName!]) {
        this.labelGrouped[item.buildingName!] = [];
      }
      let row = this.labelGrouped[item.buildingName!].find((el) => el.apartmentName === item.apartmentName);
      if (!row) {
        row = { apartmentName: item.apartmentName!, scsAddr: +item.scsAddress!, callingCode: item.callingCode ?? '', labels: [], badges: [], keypadCode: '' };
        this.labelGrouped[item.buildingName!].push(row);
      }
      row.labels.push(item.label);
      if(item.badges.length > 0) {
        row.badges = row.badges.concat(item.badges);
      }
      if(item.keypadCode !== '') {
        row.keypadCode = item.keypadCode;
      }
    });
    this.$emit('grouped', this.labelGrouped);
  }

  manageLabelsMap(item: CheckListElement) {
    if(item.selected) {
      this.labelsMap[item.data.buildingName!][item.data.apartmentName!].labels.push(item.data.label);
      item.data.badges.forEach((badge) => this.labelsMap[item.data.buildingName!][item.data.apartmentName!].badges.push(badge));
    }
    else {
      this.labelsMap[item.data.buildingName!][item.data.apartmentName!].labels = this.labelsMap[item.data.buildingName!][item.data.apartmentName!].labels.filter((el) => el.id !== item.data.label.id);
      item.data.badges.forEach((badge) => this.labelsMap[item.data.buildingName!][item.data.apartmentName!].badges = this.labelsMap[item.data.buildingName!][item.data.apartmentName!].badges.filter((el) => el !== badge));
    }
  }

  checkIfDisabled(element: CheckListElement) {
    const dataToCheck = element.data;
    const labelLimit = this.isCallingMode ? 1 : 10;
    const badgeLimit = 10;
    const maxLabelsReached = this.labelsMap[dataToCheck.buildingName!][dataToCheck.apartmentName!].labels.length >= labelLimit && 
      !this.labelsMap[dataToCheck.buildingName!][dataToCheck.apartmentName!].labels.includes(dataToCheck.label);
    const maxBadgesReached = this.labelsMap[dataToCheck.buildingName!][dataToCheck.apartmentName!].badges.length >= badgeLimit &&
      !this.labelsMap[dataToCheck.buildingName!][dataToCheck.apartmentName!].badges.some((b) => dataToCheck.badges.includes(b));
    const wouldReachMaxBadges = this.labelsMap[dataToCheck.buildingName!][dataToCheck.apartmentName!].badges.length + dataToCheck.badges.length > badgeLimit && 
      !this.labelsMap[dataToCheck.buildingName!][dataToCheck.apartmentName!].badges.some((b) => dataToCheck.badges.includes(b));
    element.isDisabled = maxLabelsReached || maxBadgesReached || wouldReachMaxBadges;
    return element.isDisabled;
  }

  handleButtonCancelAction() {
    if (this.dialogStep === 2) {
      this.dialogStep = 1;
    } else {
      this.$emit('hide-dialog');
      this.labelsMap = {};
      this.existingApartment = [];
      this.noMatch = [];
    }
  }

  formatLabel(item: SingleLabelApartment) {
    const label = item.label;
    let result = '';
    if (this.isCallingMode && item.callingCode !== undefined && item.callingCode !== '') {
      result += item.callingCode
    }
    if (label.line1 !== undefined && label.line1 !== '') {
      if (result.length > 0) {
        result += ' | ';
      }
      result += label.line1 + ' ';
    }
    if (label.line2 !== undefined && label.line2 !== '') {
      if (result.length > 0) {
        result += ' ';
      }
      result += label.line2;
    }
    return result;
  }

  buildRecap() {
    this.recap = [];

    for (const buildingKey in this.labelGrouped) {
      const building = this.labelGrouped[buildingKey];
      for (const apartment of building) {
        const apartmentName = apartment.apartmentName;
        const newLabels = apartment.labels.map((label) => {
          return new ApartmentLabelDTO({
            surname: label.type === LabelType.Private ? label.line1 : undefined,
            name: label.type === LabelType.Private ? label.line2 : undefined,
            companyName: label.type === LabelType.Business ? label.line1 : undefined,
            isPrivate: label.type === LabelType.Private,
            labelType: label.type,
            information: label.info,
          });
        });

        const newScsAddr = apartment.scsAddr;
        const newCode = this.isCallingMode ? (apartment.callingCode ?? '') : '';
        const newKeycode = apartment.keypadCode ?? '';
        const newBadgesList = apartment.badges ?? [];

        const oldBuilding = this.existBuildingSameName(buildingKey);
        if (!oldBuilding) continue;
        const oldApartment = this.existApartmentSameName(oldBuilding, apartmentName);
        if (!oldApartment) continue;

        const oldScsAddr = oldApartment.scsAddr!;
        const oldCode = oldApartment.code ?? '';
        const oldLabels = oldApartment?.labels?.map((label) => label) || [];
        const oldKeycode = oldApartment.keypadCode || '';
        const oldBadgesList = oldApartment.badgeList || [];

        this.recap.push({
          buildingName: buildingKey,
          apartmentName: apartmentName,
          newScsAddr: newScsAddr,
          oldScsAddr: oldScsAddr,
          newCode: newCode,
          oldCode: oldCode,
          newLabels: newLabels,
          oldLabels: oldLabels,
          newKeycode: newKeycode,
          oldKeycode: oldKeycode,
          oldBadges: oldBadgesList,
          newBadges: newBadgesList,
        });
      }
    }

    this.checkOverwrite();
  }

  checkOverwrite() {
  this.overwrited = [];
  this.recap.forEach((recap) => {
      let scsDiff = false;
      let labelDiff = false;
      let codeDiff = false;
      let badgeDiff = false;
      let callingCodeDiff = false;

      if (recap.oldScsAddr !== recap.newScsAddr) {
        scsDiff = true;
      }
      if (recap.oldKeycode !== recap.newKeycode) {
        codeDiff = true;
      }
      if (recap.oldBadges.length !== recap.newBadges.length) {
        badgeDiff = true;
      }
      //check if badges are different
      if (!badgeDiff) {
        badgeDiff = recap.oldBadges.some((oldBadge) => !recap.newBadges.includes(oldBadge)) 
          || recap.newBadges.some((newBadge) => !recap.oldBadges.includes(newBadge));
      }

      const oldLabels = recap.oldLabels;
      const newLabels = recap.newLabels;

      if (oldLabels.length !== newLabels.length) {
        labelDiff = true;
      }
      if(!labelDiff) {
        for (let i = 0; i < oldLabels.length; i++) {
          const oldLine1 = oldLabels[i].isPrivate ? oldLabels[i].surname?.trim() || '' : oldLabels[i].companyName?.trim() || '';
          const oldLine2 = oldLabels[i].isPrivate ? oldLabels[i].name?.trim() || '' : '';
          const newLine1 = newLabels[i].isPrivate ? newLabels[i].surname : newLabels[i].companyName;
          const newLine2 = newLabels[i].isPrivate ? newLabels[i].name : '';

          if ((oldLine1 !== newLine1) || (oldLine2 !== newLine2)) {
            labelDiff = true;
          }
        }
      }

      if (this.isCallingMode && recap.oldCode.trim() !== recap.newCode.trim()) {
        callingCodeDiff = true;
      }

      if (scsDiff || labelDiff || codeDiff || badgeDiff || callingCodeDiff) {
        this.overwrited.push({
          recap: recap,
          diffScs: scsDiff,
          diffLabel: labelDiff,
          diffCode: codeDiff,
          diffBadge: badgeDiff,
          diffCallingCode: callingCodeDiff,
        });
      }
    });
  }

  formatCallingCode(code: string) {
    if (!code) {
      return this.$t("AppResource.None");
    }
    return code;
  }
  
  formatApLabels(labels: ApartmentLabelDTO[]) {
    let result = '';
    for (const [index, label] of labels.entries()) {
      if (label.isPrivate) {
        if (label.surname && label.surname !== '') {
          result += label.surname;
        }
        if (label.name && label.name !== '') {
          if (result.length > 0) {
            result += ' ';
          }
          result += label.name;
        }
      } else {
        if (label.companyName && label.companyName !== '') {
          result += label.companyName;
        }
      }
      if (index !== labels.length - 1) {
        result += ', ';
      }
    }
    if (result === '') {
      return this.$t("AppResource.None");
    }
    return result
  }

  formatCode(code: string) {
    if (code === '') {
      return this.$t("AppResource.None");
    }
    return code;
  }

  formatBadges(badges: string[]) {
    let result = badges.filter((badge) => badge !== '').join(', ');
    if (result === '') {
      return this.$t("AppResource.None");
    }
    return result;
  }

  existBuildingSameName(buildingName: string | undefined) {
    if (!buildingName) return false;
    const result = this.entrancePlantDetail.buildings?.find((building) => 
      building.name?.trim().toLowerCase() === buildingName.toLowerCase());
    if (!result) return false;
    return result;
  }

  existApartmentSameName(buildingName: BuildingDTO, apartmentName: string | undefined) {
    if (!apartmentName) return false;
    const result = buildingName.apartments?.find((apartment) => 
      apartment.name?.trim().toLowerCase() === apartmentName.toLowerCase());
    if (!result) return false;
    return result;
  }

  checkScsAddress(item: SingleLabelApartment) {
    //check if same ap has already a imported item with scs address
    const sameApartm = this.firstStepList.find((el) => el.data.buildingName === item.buildingName && el.data.apartmentName === item.apartmentName);
    if (sameApartm) { item.scsAddress = sameApartm.data.scsAddress; return }
    //check if scs address is empty or not a number
    if (isNaN(Number(item.scsAddress))) { throw new Error(`${this.$t('import.errorScsEmptyOrNotNumber')} | ${this.$t('import.errorLine', {line: item.id})}`)}
    const scsNum = Number(item.scsAddress);
    if (scsNum < 1 || scsNum > 3999) { throw new Error(`${this.$t('import.errorScsOutOfRange')} | ${this.$t('import.errorLine', {line: item.id})}`)}
    //check if scs address is already in the imported data
    const scsAddressExistInImportedData = this.firstStepList.some((el) => Number(el.data.scsAddress) === Number(item.scsAddress));
    if (scsAddressExistInImportedData) { throw new Error(`${this.$t('import.errorScsAlreadyImported')} | ${this.$t('import.errorLine', {line: item.id})}`)}
  }

  checkCallingCode(item: SingleLabelApartment) {
    const callingCodeExistInImportedData = this.firstStepList.some((el) => el.data.callingCode === item.callingCode);
    if (callingCodeExistInImportedData) {
      throw new Error(`${this.$t('import.errorCallingCodeAlreadyImported')} | ${this.$t('import.errorLine', {line: item.id})}`);
    }
  }

  checkBadges(item: SingleLabelApartment) {
    if(item.badges.length > 0) {
      const importedbadge = this.firstStepList.map((el) => el.data.badges).flat();
      const badgesImport = item.badges.map((badge) => badge);
      item.badges = [];
      badgesImport.forEach((badge) => {
        const badgeExistInImportedData = importedbadge.some((el) => el === badge);
        if (badgeExistInImportedData) throw new Error(`${this.$t('import.errorBadgeAlreadyImported')} | ${this.$t('import.errorLine', {line: item.id})}`);
        importedbadge.push(badge);
        item.badges.push(badge);
      });
    }
  }

  checkKeypadCode(item: SingleLabelApartment) {
    if (this.entrancePlantDetail.keypadCodePasspartoutMode === PasspartoutModes.SameEntirePlant) {
      throw new Error(`${this.$t('import.errorKeypadCodePasspartoutMode')} | ${this.$t('import.errorLine', {line: item.id})}`);
    }
    //check if keypad code is empty
    if (item.keypadCode === '') return;
    //check if same ap has already a imported item with keypad code
    const importedApartmentAlreadyHasKeypadCode = this.firstStepList.find((el) => el.data.buildingName === item.buildingName && el.data.apartmentName === item.apartmentName && el.data.keypadCode !== '');
    if (importedApartmentAlreadyHasKeypadCode) { item.keypadCode = '';  return }

    if (this.isCreationMode && this.creationCodeLength === 0) {
      this.creationCodeLength = item.keypadCode.length;
    } 
    const codeLength = this.isCreationMode ? this.creationCodeLength : this.entrancePlantDetail.keypadCodeRequiredLength;
    //check if keypad code is correct
    const keypadCodeCheck = isCodeCorrectValue(item.keypadCode, codeLength);
    if (keypadCodeCheck !== KeypadCodeValidation.OK) {
      if (this.isCreationMode && (keypadCodeCheck === KeypadCodeValidation.KO_TooLong || keypadCodeCheck === KeypadCodeValidation.KO_TooShort)) {
        //in creation mode we ignore codes that doesn't respect the length set by the first code seen in the imported data
        item.keypadCode = '';
        return;
      } else {
        throw new Error(`${keypadCodeValidationToString(keypadCodeCheck)} | ${this.$t('import.errorLine', {line: item.id})}`);
      }
    }
    //check if keypad code is already in the imported data
    const keypadExistInImportedData = this.firstStepList.some((el) => el.data.keypadCode === item.keypadCode);
    if(keypadExistInImportedData) throw new Error(`${this.$t('import.errorKeypadAlreadyImported')} | ${this.$t('import.errorLine', {line: item.id})}`);
  }

  checkErrors() {
    if (!this.isCreationMode) {
      this.errorsType.scsAdrError = this.checkScsAddressError();
      this.errorsType.callingCodeError = this.checkCallingCodeError();
      this.errorsType.keypadError = this.checkKeypadCodeError();
      this.errorsType.badgesError = this.checkBadgeError();
    } else {
      this.errorsType.scsAdrError = {error: false};
      this.errorsType.callingCodeError = {error: false};
      this.errorsType.keypadError = {error: false};
      this.errorsType.badgesError = {error: false};
    }
  }

  checkScsAddressError() {
    const scsAddressList: {[scsAddress: number]: {buildingName: string, apartmentName: string}} = {};
    this.entrancePlantDetail.buildings?.forEach((building) => {
      building.apartments?.forEach((apartment) => {
        if (apartment.scsAddr) {
          scsAddressList[apartment.scsAddr] = {buildingName: building.name!, apartmentName: apartment.name!};
        }
      });
    });

    const importedScsList: {[buildingName: string]: {[apartmentName: string]: number }} = {};
    for (const buildingKey in this.labelGrouped) {
      const building = this.labelGrouped[buildingKey];
      for (const apartment of building) {
        if (!importedScsList[buildingKey]) {
          importedScsList[buildingKey] = {};
        }
        importedScsList[buildingKey][apartment.apartmentName] = apartment.scsAddr;
      }
    }

    //check if scs address is already in the plant
    for (const buildingKey in importedScsList) {
      const building = importedScsList[buildingKey];
      for (const apartmentName in building) {
        // check if is used in plant
        if (!scsAddressList[building[apartmentName]]) continue;
        // scs is used, check if from the same apartment
        if (scsAddressList[building[apartmentName]].buildingName === buildingKey && 
            scsAddressList[building[apartmentName]].apartmentName === apartmentName) continue;
        
        // if we are here the scs address is used from another apartment 
        // we need to check if the other apartment is in the importedScsList meaning that the scs is changing for the other apartment too
        // otherwise we have an error
        const otherAp = scsAddressList[building[apartmentName]];
        if (!importedScsList[otherAp.buildingName] || !importedScsList[otherAp.buildingName][otherAp.apartmentName]) {
          const result: ErrorAndCause = {
            error: true, 
            importedValue: [this.$t("AppResourceEP.ScsAddressAlreadyInUse"), building[apartmentName].toString()],
            importedAp: buildingKey + ' ' + apartmentName,
            dbApartment: otherAp.buildingName + ' ' + otherAp.apartmentName
          };
          return result
        }
      }
    }
    return { error: false, importedValue: [], importedAp: '', dbApartment: ''};
  }

  checkCallingCodeError() {
    if (!this.isCallingMode) {
      return {error: false, importedValue: [], importedAp: '', dbApartment: ''};
    }
    const callingCodeList: {[callingCode: string]: {buildingName: string, apartmentName: string}} = {};
    this.entrancePlantDetail.buildings?.forEach((building) => {
      building.apartments?.forEach((apartment) => {
        if (apartment.code) {
          callingCodeList[apartment.code] = {buildingName: building.name!, apartmentName: apartment.name!};
        }
      });
    });

    const importedCallingCode: {[buildingName: string]: {[apartmentName: string]: string }} = {};
    for (const buildingKey in this.labelGrouped) {
      const building = this.labelGrouped[buildingKey];
      for (const apartment of building) {
        const callingCode = apartment.callingCode;
        if (callingCode) {
          if (!importedCallingCode[buildingKey]) {
            importedCallingCode[buildingKey] = {};
          }
          importedCallingCode[buildingKey][apartment.apartmentName] = callingCode;
        }
      }
    }

    //check if calling code is already in the plant
    for (const buildingKey in importedCallingCode) {
      const building = importedCallingCode[buildingKey];
      for (const apartmentName in building) {
        // check if is used in plant
        if (!callingCodeList[building[apartmentName]]) continue;
        // calling code is used, check if from the same apartment
        if (callingCodeList[building[apartmentName]].buildingName === buildingKey && 
          callingCodeList[building[apartmentName]].apartmentName === apartmentName) continue;
        
        // if we are here the calling code is used from another apartment 
        // we need to check if the other apartment is in the importedCallingCode meaning that the code is changing for the other apartment too
        // otherwise we have an error
        const otherAp = callingCodeList[building[apartmentName]];
        if (!importedCallingCode[otherAp.buildingName] || importedCallingCode[otherAp.buildingName][otherAp.apartmentName] === undefined) {
          const result: ErrorAndCause = {
            error: true,
            importedValue: [this.$t("AppResourceEP.AnotherApartmentHasSameCallingCode"), building[apartmentName]],
            importedAp: buildingKey + ' ' + apartmentName,
            dbApartment: otherAp.buildingName + ' ' + otherAp.apartmentName
          };
          return result;
        }
      }
    }
    return {error: false, importedValue: [], importedAp: '', dbApartment: ''}; 
  }

  checkKeypadCodeError() {
    const keypadCodeList: {[keypadCode: string]: {buildingName: string, apartmentName: string}} = {};
    this.entrancePlantDetail.buildings?.forEach((building) => {
      building.apartments?.forEach((apartment) => {
        if (apartment.keypadCode) {
          keypadCodeList[apartment.keypadCode] = {buildingName: building.name!, apartmentName: apartment.name!};
        }
      });
    });

    const importedKeycode: {[buildingName: string]: {[apartmentName: string]: string }} = {};
    for (const buildingKey in this.labelGrouped) {
      const building = this.labelGrouped[buildingKey];
      for (const apartment of building) {
        if (!importedKeycode[buildingKey]) {
          importedKeycode[buildingKey] = {};
        }
        importedKeycode[buildingKey][apartment.apartmentName] = apartment.keypadCode;
      }
    }

    //check if keypad code is already in the plant
    for (const buildingKey in importedKeycode) {
      const building = importedKeycode[buildingKey];
      for (const apartmentName in building) {
        // check if is used in plant
        if (!keypadCodeList[building[apartmentName]]) continue;
        // keypad code is used, check if from the same apartment
        if (keypadCodeList[building[apartmentName]].buildingName === buildingKey && 
          keypadCodeList[building[apartmentName]].apartmentName === apartmentName) continue;
        
        // if we are here the keypad code is used from another apartment 
        // we need to check if the other apartment is in the importedKeycode meaning that the code is changing for the other apartment too
        // otherwise we have an error
        const otherAp = keypadCodeList[building[apartmentName]];
        if (!importedKeycode[otherAp.buildingName] || importedKeycode[otherAp.buildingName][otherAp.apartmentName] === undefined) {
          const result: ErrorAndCause = {
            error: true,
            importedValue: [this.$t("AppResourceEP.KeypadCodeAlreadyExistsInPlant"), building[apartmentName]],
            importedAp: buildingKey + ' ' + apartmentName,
            dbApartment: otherAp.buildingName + ' ' + otherAp.apartmentName
          };
          return result;
        }
      }
    }
    return {error: false, importedValue: [], importedAp: '', dbApartment: ''}; 
  }

  checkBadgeError() {
    const badgeList: {[badge: string]: {buildingName: string, apartmentName: string}} = {};
    this.entrancePlantDetail.buildings?.forEach((building) => {
      building.apartments?.forEach((apartment) => {
        apartment.badgeList?.forEach((badge) => {
          badgeList[badge] = {buildingName: building.name!, apartmentName: apartment.name!};
        });
      });
    });

    const importedBadges: {[buildingName: string]: {[apartmentName: string]: string[] }} = {};
    for (const buildingKey in this.labelGrouped) {
      const building = this.labelGrouped[buildingKey];
      for (const apartment of building) {
        if (!importedBadges[buildingKey]) {
          importedBadges[buildingKey] = {};
        }
        importedBadges[buildingKey][apartment.apartmentName] = apartment.badges;
      }
    }

    //check if badge is already in the plant
    for (const buildingKey in importedBadges) {
      const building = importedBadges[buildingKey];
      for (const apartmentName in building) {
        for (const badge of building[apartmentName]) {
          // check if is used in plant
          if (!badgeList[badge]) continue;
          // badge is used, check if from the same apartment
          if (badgeList[badge].buildingName === buildingKey && 
            badgeList[badge].apartmentName === apartmentName) continue;
          
          // if we are here the badge is used from another apartment 
          // we need to check if the other apartment is in the importedBadges meaning that the badge is changing for the other apartment too
          // otherwise we have an error
          const otherAp = badgeList[badge];
          if (!importedBadges[otherAp.buildingName] || !importedBadges[otherAp.buildingName][otherAp.apartmentName]) {
            const result: ErrorAndCause = {
              error: true,
              importedValue: [this.$t("AppResourceEP.BadgeAlreadyPaired"), badge],
              importedAp: buildingKey + ' ' + apartmentName,
              dbApartment: otherAp.buildingName + ' ' + otherAp.apartmentName
            };
            return result
          }
        }
      }
    }
    return {error: false, importedValue: [], importedAp: '', dbApartment: ''};
  }

  prepareToSend() {
    const importLabelItems = this.recap.map((item) => {
      const building = this.existBuildingSameName(item.buildingName);
      const buildingId = building ? building.buildingId : undefined;
      let apartmentId: string | undefined = undefined;
      if (building) {
        const apartment = this.existApartmentSameName(building, item.apartmentName);
        apartmentId = apartment ? apartment.apartmentId : undefined;
      }
      const labels = item.newLabels.map((label) => new ApartmentLabelDTO({ ...label }));
      const keypadCode = item.newKeycode !== '' ? item.newKeycode : undefined;
      const badges = item.newBadges.filter((badge) => badge !== '');
      const code = this.isCallingMode && item.newCode !== '' ? item.newCode : undefined;
      return new ApartmentDTO({
        buildingId: buildingId,
        apartmentId: apartmentId,
        name: item.apartmentName,
        scsAddr: item.newScsAddr,
        code: code,
        labels: labels,
        keypadCode: keypadCode,
        badgeList: badges,
      });
    });

    return new BuildingImportDto({ apartments: importLabelItems.filter((item) => item !== undefined) });
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  showPopupError() {
    const errors = this.errorsType;
    this.popupErrorMessage = '';
    if (errors.badgesError.error) {
      this.popupErrorMessage = this.$t('import.importBadgesError');
      this.popupErrorValues = errors.badgesError;
    }
    if (errors.keypadError.error) {
      this.popupErrorMessage = this.$t('import.importKeypadError');
      this.popupErrorValues = errors.keypadError;
    }
    if (errors.scsAdrError.error) {
      this.popupErrorMessage = this.$t('import.importScsError');
      this.popupErrorValues = errors.scsAdrError;
    }
    if (errors.callingCodeError.error) {
      this.popupErrorMessage = this.$t('import.importCallingCodeError');
      this.popupErrorValues = errors.callingCodeError;
    }
    this.isPopupErrorVisible = true;
  }

  hidePopupError() {
    this.isPopupErrorVisible = false;
    this.popupErrorMessage = '';
    this.popupErrorValues = { error: false, importedValue: [], importedAp: '', dbApartment: '' };
  }
}
